import { SVButton, SVLoader, SVTabs } from '@/components/core';
import ExternalAccountsList from '@/components/externalAccounts/ExternalAccountsList';
import DefaultLayout from '@/components/layouts/DefaultLayout';
import { ROUTES } from '@/constants/routes';
import { useGetCustomerDetails } from '@/hooks/requests/useCustomers';
import { useGetExternalAccountsList } from '@/hooks/requests/useExternalAccounts';
import { ExternalBankAccountStatus } from '@/types/externalAccounts';
import { createFullName } from '@/utils/string';
import { Card, Typography } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';

export default function CustomersExternalAccountsListPage() {
  const { uuid = '' } = useParams();
  const navigate = useNavigate();
  const { customerDetails, isCustomerDetailsPending } =
    useGetCustomerDetails(uuid);
  const { aggregateInfo } = useGetExternalAccountsList({
    consumer_ids: [uuid],
  });

  if (isCustomerDetailsPending) {
    return (
      <DefaultLayout header="">
        <Card>
          <SVLoader />
        </Card>
      </DefaultLayout>
    );
  }

  if (!customerDetails) {
    return (
      <DefaultLayout header="">
        <Card>
          <Typography>
            Something went wrong retreiving the customer. Our team has been
            notified.
          </Typography>
        </Card>
      </DefaultLayout>
    );
  }

  const customerName = createFullName(
    customerDetails.firstName,
    customerDetails.lastName,
  );

  return (
    <DefaultLayout
      header="External Accounts"
      buttons={[
        <SVButton
          key="add"
          onClick={() =>
            navigate(
              `${ROUTES.CUSTOMERS}/${uuid}${ROUTES.EXTERNAL_ACCOUNTS}/add`,
            )
          }>
          Add
        </SVButton>,
      ]}
      breadcrumb={[
        {
          label: 'Customers',
          href: ROUTES.CUSTOMERS,
        },
        {
          label: customerName,
          href: `${ROUTES.CUSTOMERS}/${customerDetails.uuid}`,
        },
        {
          label: 'External Accounts',
          isActive: true,
        },
      ]}>
      <Card>
        <SVTabs
          headers={[
            `Awaiting E-Sign (${aggregateInfo?.totalAwaitingESign})`,
            `Submitted (${aggregateInfo?.totalSubmitted})`,
            `Verified (${aggregateInfo?.totalVerified})`,
            `Declined (${aggregateInfo?.totalDeclined})`,
          ]}
          panels={[
            <ExternalAccountsList
              key="pending"
              status={ExternalBankAccountStatus.PENDING}
              customerId={uuid}
            />,
            <ExternalAccountsList
              key="submitted"
              status={ExternalBankAccountStatus.SUBMITTED}
              customerId={uuid}
            />,
            <ExternalAccountsList
              key="verified"
              status={ExternalBankAccountStatus.VERIFIED}
              customerId={uuid}
            />,
            <ExternalAccountsList
              key="declined"
              status={ExternalBankAccountStatus.DECLINED}
              customerId={uuid}
            />,
          ]}
        />
      </Card>
    </DefaultLayout>
  );
}
